import {useState, useEffect, useMemo} from "react"
import {useSessionContext} from "../../reactContext/contextCustomHooks/useSessionContext"
import {useTeamContext} from "../../reactContext/contextCustomHooks/useTeamContext"
import axios from "axios"
import {API_ECP_PROJECTS} from "../../constants/apiUrls"
import {useTranslation} from "react-i18next"
import {useForm} from "react-hook-form"
import Dropzone from "../../components/dropzone/Dropzone"
import {toast} from "react-toastify"
import useTeam from "../../swrCustomHooks/useTeam"
import useEcpProjectSource from "../../swrCustomHooks/useEcpProjectSource"
import LoadingComponent from "../../components/loading-component/LoadingComponent"
import Select from "react-select"
import {labelMap} from "../../constants/multilingual"
import isLicenceValidity from "../../tools/isLicenceValidity"

export default function UpdateEcpProjectsForm({project, mutateEcpProject, setWorkflowNavigation, setIsPulling}) {
    const {t} = useTranslation()
    const {sessionContext} = useSessionContext()
    const {teamContext} = useTeamContext()
    const {team, isLoadingTeam, isErrorTeam} = useTeam(sessionContext.idTeam)
    const {ecpProjectSource, isLoadingEcpProjectSource, isErrorEcpProjectSource} = useEcpProjectSource(project.id)
    const languages = team?.team?.languages || []

    const {register, handleSubmit, formState: {errors}} = useForm({defaultValues: {
        isPrivate: !project.is_public,
        title: project.title,
    }})

    const allLanguages = useMemo(() => {
        return languages
            .map((lang) => ({
                value: lang.code,
                label: t(labelMap[lang.code]),
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
    }, [languages, labelMap, t])

    const [sourceLanguage, setSourceLanguage] = useState(allLanguages.find(x => x.value === project.lang_code))
    const [isSourceLanguage, setIsSourceLanguage] = useState(true)
    const [fileType, setFileType] = useState(project.file_type)
    const [uploadFile, setUploadFile] = useState(null)
    const [isUpdated, setIsUpdated] = useState(false)

    const handleFileDrop = (file) => {
        setUploadFile(file)
        setIsUpdated(true)
    }

    const updateProject = async (data) => {
        if (isUpdated) {
            if (isLicenceValidity(teamContext.licenceStartDate, teamContext.licenceEndDate)) {
                if (sourceLanguage !== undefined) {
                    setIsSourceLanguage(true)
                    const bodyRequest = {
                        is_public: !data.isPrivate,
                        title: data.title,
                        lang_code: sourceLanguage.value,
                    }
                    const headersRequest = {headers: {Authorization: `Bearer ${sessionContext.idToken}`}}
                    axios.put(`${API_ECP_PROJECTS}/${project.id}`, bodyRequest, headersRequest)
                    .then(() => {
                        if (uploadFile) {
                            setIsPulling(true)
                            axios.post(`${API_ECP_PROJECTS}/${project.id}/tries`, {step: "upload", filename: uploadFile.name},  headersRequest)
                            .then((response) => {
                                axios.put(response.data.uploadUrl, uploadFile)
                                .then(() => {
                                    toast.success(t("Upload completed!"))
                                })
                                .catch((error) => {
                                    console.error("PUT /projects/uploadUrl: ", error)
                                    toast.error(t("An error has occurred"))
                                })
                            })
                            .catch((error) => {
                                console.error("POST /projects/tries: ", error)
                                toast.error(t("An error has occurred"))
                            })
                            setUploadFile(null)
                        }
                        mutateEcpProject()
                        toast.success(t("The changes have been applied!"))
                        setWorkflowNavigation(1)
                        setIsUpdated(false)
                    })
                    .catch((error) => {
                        console.error("PUT /projects/project: ", error)
                        toast.error(t("An error has occurred"))
                    })
                } else {
                    setIsSourceLanguage(sourceLanguage === undefined ? false : true)
                }
            } else {
                toast.error(t("Licence expired. Please contact Neurowide sales department."))
            }
        }
    }

    useEffect(() => {
        setSourceLanguage(allLanguages.find(x => x.value === project.lang_code) || null)
    }, [allLanguages, project.lang_code])

    if (isErrorTeam || isLoadingTeam || team.message
        || isErrorEcpProjectSource || isLoadingEcpProjectSource || ecpProjectSource.message
    ) return <LoadingComponent/>
    return (
        <>
        <form className="update-ecp-projects-form" onSubmit={handleSubmit(updateProject)}>
            <div className="left-content">
                <h3>{t("Project information")}</h3>
                <span className="switch-label">{t("Private")}</span>
                <label className="switch" title={t("Manage project visibility")}>
                    <input type="checkbox" id="is-private" name="is-private" {...register("isPrivate", {type: Boolean, onChange: () => {setIsUpdated(true)}})}/>
                    <span className="slider round"></span>
                </label>
                <div className="field">
                    <label>{`${t("Project name")}*`}</label>
                    <input className={`${errors.title ? "not-valid" : ""}`} type="text" id="title" name="title" placeholder="Ex. Projet 01" {...register("title", {required: true, minLength: 2, maxLength: 75, onChange: () => {setIsUpdated(true)}})}/>
                </div>
                <label>{`${t("Source language")}*`}</label>
                <Select
                    name="source-language"
                    value={sourceLanguage}
                    options={allLanguages}
                    isClearable={false}
                    isSearchable={false}
                    isDisabled={project.generate_all_ecp_status === "done" && true}
                    maxMenuHeight={110}
                    className={`basic-single ${!isSourceLanguage && "select-not-valid"}`}
                    classNamePrefix="select"
                    onChange={(value) => {
                        setSourceLanguage(value)
                        setIsUpdated(true)
                    }}
                />
                <span className="mandatory">{`*${t("Required fields")}`}</span>
            </div>
            <div className="right-content">
                {fileType === "video" && <video controls crossOrigin="anonymous" src={uploadFile ? URL.createObjectURL(uploadFile) : ecpProjectSource} type="video/mp4"></video>}
                <Dropzone type={"ecp"}
                    setUploadFile={handleFileDrop}
                    maxSize={500000000}
                    maxDuration={team.team && team.team.max_duration * 60}
                    generationStatus={project.generate_all_ecp_status}
                    fileName={project.filename}
                    uploadStatus={project.upload_status}
                    fileType={fileType}
                    setFileType={setFileType}
                    pdf={true}
                />
            </div>
            <div className="buttons">
                <button className="create-project-btn" title={t("Update")}>{t("Update")}</button>
            </div>
        </form>
        </>
    )
}